import {
    faCheckCircle,
    faExclamationTriangle,
    faHourglassStart,
    faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import Markdown from "react-markdown";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PATIENT_VISIT_BY_CSN } from "../../../amplify/appsync/query";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { callWrite, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/appsync/write";
import { readBinaryFile } from "../../../amplify/s3/storage";
import useSetPagePatientVisit from "../../common/GlobalOptionsContext";
import CustomSurveyForm from "./CustomSurveyForm";

interface DashboardProps {
    csn: string;
    tenant?: string;
}

export const COHORT_REVISIT_TEST_DASHBOARD_PATH = 'cohort_revisit_test/2025-06-26';
export const FEEDBACK_TYPE_INDEX_SUMMARY = "cohort_revisit_index_summary";
export const FEEDBACK_TYPE_REVISIT_SUMMARY = "cohort_revisit_revisit_summary";
export const FEEDBACK_TYPE_PREDICTABILITY = "cohort_revisit_predictability";
export const FEEDBACK_TYPE_REASONING_PREDICTABILITY = "cohort_revisit_reasoning_predictability";
export const FEEDBACK_TYPE_PREDICTABILITY_THEMES = "cohort_revisit_predictability_themes";
export const FEEDBACK_TYPE_PREVENTABILITY = "cohort_revisit_preventability";
export const FEEDBACK_TYPE_REASONING_PREVENTABILITY = "cohort_revisit_reasoning_preventability";
export const FEEDBACK_TYPE_PREVENTABILITY_THEMES = "cohort_revisit_preventability_themes";
export const FEEDBACK_TYPE_POST_DISCHARGE_MONITORING = "cohort_revisit_post_discharge_monitoring";
export const FEEDBACK_TYPE_REASONING_PDM = "cohort_revisit_reasoning_pdm";
export const FEEDBACK_TYPE_DCI = "cohort_revisit_dci";
export const FEEDBACK_TYPE_REASONING_DCI = "cohort_revisit_reasoning_dci";

export const CohortRevisitDashboard = ({
    csn,
    tenant = 'stanford'
}: DashboardProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<any>();
    const [feedbackTargetToSurvey, setFeedbackTargetToSurvey] = useState<any>({});
    const [errorMessage, setErrorMessage] = useState("");

    // Add patientVisit state directly in the component
    const [patientVisit, setPatientVisit] = useState<PatientVisit>();
    useSetPagePatientVisit(patientVisit);

    // Add function to fetch patient visit data
    const fetchPatientVisit = async () => {
        try {
            const patients = await callQuery({
                operation: PATIENT_VISIT_BY_CSN,
                csn: csn,
            });
            
            if (Array.isArray(patients) && patients.length > 0) {
                setPatientVisit(patients[0]);
            } else {
                setPatientVisit(undefined);
            }
        } catch (error) {
            console.error("Error fetching patient visit:", error);
            setPatientVisit(undefined);
        }
    };

    // Function to fetch dashboard data
    const fetchDashboardData = async () => {
        setIsLoading(true);
        const decoder = new TextDecoder();
        
        try {
            // Fetch the actual data
            const dashboardPath = `${COHORT_REVISIT_TEST_DASHBOARD_PATH}/${csn}.json`;
            const data = await readBinaryFile(
                dashboardPath,
                tenant,
                'ed-llm'
            );

            const jsonData = JSON.parse(decoder.decode(data));
            
            // Set all required state variables
            setData(jsonData || {});
            
            // Fetch survey data
            await fetchSurveyData();
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to fetch survey data
    const fetchSurveyData = async () => {
        try {
            console.log("Fetching survey data for CSN:", csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn,
            }) as LLMVisitFeedback[];
            
            if (feedbackItems && feedbackItems.length > 0) {
                // Get all original survey feedback items
                const originalSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'survey' && 
                        (item.feedback_target.startsWith("cohort_revisit_"))
                );

                const feedbackTargetToSurvey: any = {};

                for (let item of originalSurveyFeedbackItems) {
                    const survey = JSON.parse(item.note);
                    feedbackTargetToSurvey[item.feedback_target] = {
                        "option": survey.option,
                        "options": survey.options,
                        "comment": survey.comment,
                    };
                }

                setFeedbackTargetToSurvey(feedbackTargetToSurvey);
            }
        } catch (error) {
            console.error("Error fetching survey data:", error);
        }
    };

    const handleSave = async (feedback_target: string, data: any): Promise<boolean> => {
        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: csn,
                mrn: '',
                feedback_target: feedback_target,
                feedback_type: 'survey',
                note: JSON.stringify(data),
            });
            
            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Please refresh and try again.");
                return false;
            }

            // Refresh the survey data after submission
            await fetchSurveyData();
            
            return true;
        } catch (error) {
            console.error("Error submitting original survey:", error);
            
            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }

            return false;
        }
    };

    const getIsCompleted = () => {
        const isCompleted = feedbackTargetToSurvey[FEEDBACK_TYPE_INDEX_SUMMARY] && 
            feedbackTargetToSurvey[FEEDBACK_TYPE_REVISIT_SUMMARY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_PREDICTABILITY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_REASONING_PREDICTABILITY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_PREDICTABILITY_THEMES] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_PREVENTABILITY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_REASONING_PREVENTABILITY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_PREVENTABILITY_THEMES] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_POST_DISCHARGE_MONITORING] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_REASONING_PDM] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_DCI] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_REASONING_DCI];

        return (
            <Row>
                <Col>
                    {
                        isCompleted ? (
                            <Alert variant="success">
                                <span className="me-1"><FontAwesomeIcon icon={faCheckCircle} /></span> Survey Completed
                            </Alert>
                        ) : (
                            <Alert variant="info">
                                <span className="me-1"><FontAwesomeIcon icon={faHourglassStart} /></span> Survey Not Yet Completed
                            </Alert>
                        )
                    }
                </Col>
            </Row>
        );
    };

    const getSectionHeader = (title: string) => {
        return (
            <Row>
                <Col>
                    <div className="survey-section p-3 mb-4">
                        {title}
                    </div>
                </Col>
            </Row>
        );
    };

    const getContextSection = (title: string, data: string | string[], horizontalLineAbove: boolean = true) => {
        return (
            <>
                {horizontalLineAbove && <hr />}
                <Row className="mb-2">
                    <Col>
                        <strong>{title}</strong>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {data}
                            </div>
                        )}
                    </Col>
                </Row>
            </>
        );
    }

    const getSectionToRate = (title: string, data: string, surveyTitle: string, 
        surveyDescription: string, surveyOptions: string[], 
        surveyKey: string, isMultiSelect?: boolean, optionToValueMap?: any) => {
        return (
            <>
                <hr />
                <Row>
                    <Col>
                        <div>
                            <strong>{title}</strong>
                        </div>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {(optionToValueMap && optionToValueMap[item]) ? optionToValueMap[item] : item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {data}
                            </div>
                        )}
                    </Col>
                    <Col>
                        <CustomSurveyForm
                            title={surveyTitle}
                            altTitle={title}
                            description={surveyDescription}
                            options={surveyOptions}
                            onSubmit={(data) => handleSave(surveyKey, data)}
                            enableComment={true}
                            initialSelectedOption={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].option : undefined}
                            initialSelectedOptions={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].options : undefined}
                            initialComment={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].comment : undefined}
                            isMultiSelect={isMultiSelect}
                        />
                    </Col>
                </Row>
            </>
        )
    }

    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (csn) {
            fetchDashboardData();
            fetchPatientVisit();
        }
    }, [csn, tenant]);

    if (isLoading) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading dashboard data...
                </Col>
            </Row>
        );
    }
    
    if (errorMessage) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <Alert variant="danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
                    </Alert>
                </Col>
            </Row>
        );
    }

    if (!data) {
        return <></>
    }

    return (
        <Container>
            <Row className="mb-3">
                <Col>
                    <div>
                        <Row>
                            <Col>
                                <h5>Cohort Revisit Rating</h5>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <small>
                                    <strong>CSN</strong> {csn} &bull; <strong>MRN</strong> {data.MRN}
                                </small>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
            {getIsCompleted()}
            {getContextSection("Index ED Note", data.Note_index, false)}
            {getContextSection("Index DC Instructions", data.DCI_index)}
            {getContextSection("Revisit ED Note", data.Note_revisit)}
            {getSectionToRate("Index Visit Summary", data.index_summary, "Accuracy", 
                "Please rate the accuracy of this generated summary.", 
                ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_INDEX_SUMMARY)}

            {getContextSection("Index Diagnosis", data.Dx_index)}
            {getContextSection("Days Between Visits", data.Days_between_visits)}
            {getSectionToRate("Revisit Summary", data.revisit_summary, "Accuracy", 
                "Please rate the accuracy of this generated summary.", 
                ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_REVISIT_SUMMARY)}

            {getContextSection("Revisit Diagnosis", data.Dx_revisit)}
            {getContextSection("Revisit Disposition", data.Dispo_class_ADT_revisit)}
            {getSectionToRate(
                "Predictability of Revisit", 
                data.predictability, 
                "MD assessment of predictability", 
                "Please rate your assessment of predictability:", 
                ['Very likely predictable', 'Likely Predictable', 'Possibly Predictable', 'Unlikely Predictable', 'Very Unlikely Predictable'], 
                FEEDBACK_TYPE_PREDICTABILITY
            )}

            {getSectionToRate(
                "Predictability Reasoning", 
                data.reasoning_predictability, 
                "Reasoning Quality for Predictability", 
                "Please rate the quality of the reasoning provided for the predictability assessment.", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_REASONING_PREDICTABILITY
            )}

            {getSectionToRate(
                "Predictability Themes", 
                data.predictability_themes, 
                "Relevant Predictability Themes", 
                "Select all relevant themes that contributed to the predictability of this revisit.", 
                [
                    'Unstable chronic disease',
                    'Uncontrolled symptoms at index discharge',
                    'Known adherence barriers',
                    'Incomplete workup or management at index visit',
                    'Revisit for adverse effects of new therapy',
                    'Revisit for planned follow up'
                ], 
                FEEDBACK_TYPE_PREDICTABILITY_THEMES,
                true,
                {
                    'unstable_chronic_disease': 'Unstable chronic disease',
                    'uncontrolled_sxs_at_dc': 'Uncontrolled symptoms at index discharge',
                    'adherence_barriers': 'Known adherence barriers',
                    'incomplete_workup_mgmt': 'Incomplete workup or management at index visit',
                    'adverse_effects': 'Revisit for adverse effects of new therapy',
                    'planned_fup': 'Revisit for planned follow up',
                    'high_risk_pt': 'High risk patient (e.g. frail, elderly with chronic high risk condition)',
                    'sdoh': 'Other'
                }
            )}

            {getSectionToRate(
                "Preventability of Revisit", 
                data.preventability, 
                "MD Assessment of Preventability", 
                "Please rate your assessment of whether this revisit was preventable.", 
                [
                    'Very likely preventable',
                    'Likely preventable',
                    'Possibly preventable',
                    'Unlikely preventable',
                    'Very unlikely preventable'
                ], 
                FEEDBACK_TYPE_PREVENTABILITY
            )}

            {getSectionToRate(
                "Preventability Reasoning", 
                data.reasoning_preventability, 
                "Reasoning Quality for Preventability", 
                "Please rate the quality of the reasoning provided for the preventability assessment.", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_REASONING_PREVENTABILITY
            )}

            {getSectionToRate(
                "Preventability Themes", 
                data.preventability_themes, 
                "Relevant Preventability Themes", 
                "Select all relevant themes that contributed to the preventability of this revisit.", 
                [
                    'Inadequate DC therapeutic regimen',
                    'Inadequate DC guidance on expected symptoms/course',
                    'Unaddressed social determinants of health (e.g. homelessness, transportation)',
                    'Inadequate outpatient follow-up plan',
                    'High risk patient (e.g. frail, elderly with chronic high risk condition)',
                    'Incomplete ED diagnosis or management'
                ],
                FEEDBACK_TYPE_PREVENTABILITY_THEMES,
                true,
                {
                    'dc_meds': 'Inadequate DC therapeutic regimen',
                    'dc_guidance': 'Inadequate DC guidance on expected symptoms/course',
                    'uncontrolled_sxs_at_dc': 'Unaddressed social determinants of health (e.g. homelessness, transportation)',
                    'outpatient_coordination': 'Inadequate outpatient follow-up plan',
                    'high_risk_pt': 'High risk patient (e.g. frail, elderly with chronic high risk condition)',
                    'incomplete_ed_dx_rx': 'Incomplete ED diagnosis or management',
                    'unstable_chronic_disease': 'Unstable chronic disease',
                    'adherence_barriers': 'Known adherence barriers',
                    'adverse_effects': 'Revisit for adverse effects of new therapy',
                    'sdoh': 'Other'
                }
            )}

            {getSectionToRate(
                "Post-Discharge Monitoring", 
                data.post_discharge_monitoring, 
                "Could the revisit have been better predicted or prevented with appropriate post-discharge monitoring?", 
                "Please rate the relevance of post-discharge monitoring for this revisit.", 
                [
                    'Very likely helpful',
                    'Likely helpful',
                    'Possibly helpful',
                    'Unlikely helpful',
                    'Very unlikely helpful'
                ], 
                FEEDBACK_TYPE_POST_DISCHARGE_MONITORING
            )}

            {getSectionToRate(
                "Post-Discharge Monitoring Reasoning", 
                data.reasoning_pdm, 
                "Reasoning Quality for Post-Discharge Monitoring", 
                "Please rate the quality of the reasoning provided for the relevance of post-discharge monitoring.", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_REASONING_PDM
            )}

            {getSectionToRate(
                "Discharge Instructions", 
                data.dci, 
                "Could the revisit have been prevented with better DC instructions, including interactive/longitudinal interaction after the index visit?", 
                "Please rate the relevance of DC instructions and follow-up.", 
                [
                    'Very likely helpful',
                    'Likely helpful',
                    'Possibly helpful',
                    'Unlikely helpful',
                    'Very unlikely helpful'
                ], 
                FEEDBACK_TYPE_DCI
            )}

            {getSectionToRate(
                "Discharge Instructions Reasoning", 
                data.reasoning_dci, 
                "Reasoning Quality for DC Instructions", 
                "Please rate the quality of the reasoning provided for the relevance of DC instructions.", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_REASONING_DCI
            )}

        </Container>
    );
};
